<template>
  <chart-overwrite-modal-base
    v-if="earlyBookingOverwriteMetadata.earlyBooking"
    ref="baseModal"
    :form-data="earlyBookingOverwriteMetadata"
    :modal-id="earlyBookingOverwriteMetadata.earlyBooking.date"
    :edit-date="earlyBookingOverwriteMetadata.earlyBooking.date"
    :show-reset-btn="
      earlyBookingOverwriteMetadata.earlyBooking.hasCustomEarlyBookingDiscount()
    "
    :reset-value="
      earlyBookingOverwriteMetadata.earlyBooking.earlyBookingDiscount
        .toFixed(3)
        .toString()
    "
    :submittable="showInfoBox"
    @overwrite="overwrite"
    @reset="reset"
  >
    <template #title>
      {{ title }}
    </template>

    <template #help>
      <!-- valdem factor help tooltip-->
      <v-row v-if="activeHelpBoxIdentifier === 'valdemFactorHelp'">
        <v-col cols="8">
          <h4>{{ $t('earlyBookingOverwriteModal.valDem') }}</h4>
        </v-col>
        <v-col cols="12" class="d-flex justify-space-between">
          <span class="caption white-opacity-text">
            {{ $t('earlyBookingOverwriteModal.currentEarlyBookingDiscount') }}
          </span>
          <span class="caption white-opacity-text">
            {{
              earlyBookingOverwriteMetadata.earlyBooking.getCurrentEarlyBookingDiscount()
            }}
          </span>
        </v-col>
        <!--  @todo add these vals-->
        <v-col v-if="false" cols="12" class="d-flex justify-space-between">
          <span class="caption white-opacity-text">
            {{ $t('earlyBookingOverwriteModal.timeFactor') }}
          </span>
          <span v-if="false" class="caption white-opacity-text"> FFF </span>
        </v-col>
        <v-col v-if="false" cols="12" class="d-flex justify-space-between">
          <span class="caption white-opacity-text">
            {{ $t('earlyBookingOverwriteModal.realizedDemandFactor') }}
          </span>
          <span v-if="false" class="caption white-opacity-text"> FFF </span>
        </v-col>
      </v-row>
    </template>

    <template #infobox>
      <v-fade-transition>
        <v-row v-if="showInfoBox" no-gutters>
          <v-col class="py-0">
            <v-alert class="w-100 py-0 mb-0" color="primary" dense outlined>
              <span class="slider-text">
                {{ $t('earlyBookingOverwriteModal.infoBox') }}
              </span>
            </v-alert>
          </v-col>
        </v-row>
      </v-fade-transition>
    </template>

    <template #body>
      <v-row class="no-gutters font-weight-light">
        <v-col cols="8" class="border-bottom-gray">
          {{ $t('earlyBookingOverwriteModal.cashDeskPrice') }}
        </v-col>
        <v-col cols="4" class="border-bottom-gray d-flex justify-space-between">
          {{ calculationSteps.prognosis.cashDeskPrice | currency }}
        </v-col>

        <v-col cols="8" class="border-bottom-primary">
          {{ $t('earlyBookingOverwriteModal.valueAndDemandFactor') }}
        </v-col>
        <v-col
          cols="4"
          class="border-bottom-primary d-flex justify-space-between"
        >
          {{ calculationSteps.prognosis.valdemAbsolute | currency }}
          <i
            class="pt-1 fas fa-question clickable"
            @mouseover="openHelpTooltip(true, 'valdemFactorHelp')"
            @mouseleave="openHelpTooltip(false)"
          >
          </i>
        </v-col>

        <!-- round diff -->
        <v-col
          v-if="calculationSteps.prognosis.currentPostedPriceRoundDiff"
          class="col-12"
        >
          <v-row class="no-gutters border-bottom-gray">
            <v-col class="col-8">
              {{ $t('multiDayDiscountOverwriteModal.roundDiff') }}
            </v-col>
            <v-col class="col-4">
              {{
                calculationSteps.prognosis.currentPostedPriceRoundDiff
                  | currency
              }}
            </v-col>
          </v-row>
        </v-col>

        <!-- current posted price -->
        <v-col cols="8" class="border-bottom-primary">
          {{ $t('earlyBookingOverwriteModal.currentPostedPrice') }}
        </v-col>
        <v-col cols="4" class="border-bottom-primary">
          {{ calculationSteps.prognosis.currentPostedPriceRounded | currency }}
        </v-col>
      </v-row>

      <override-slider
        :disabled="sliderStepSize >= maxEarlyBooking"
        :sliderValue="newEarlyBookingFactor"
        :sliderStepSize="sliderStepSize"
        :decimals="3"
        :minValue="0"
        :maxValue="
          earlyBookingOverwriteMetadata.earlyBooking.maxEarlyBookingDiscount
        "
        :minTitle="$t('earlyBookingOverwriteModal.min')"
        :maxTitle="$t('earlyBookingOverwriteModal.max')"
        :sliderRules="[
          (nextEarlyBookingDiscount) =>
            earlyBookingOverwriteMetadata.earlyBooking.getCurrentEarlyBookingDiscount() <
              nextEarlyBookingDiscount ||
            !showInfoBox ||
            $i18n.t('earlyBookingOverwriteModal.targetPriceTooLow', {
              val: $options.filters.centimesToFr(
                calculationSteps.current.currentPostedPrice,
                true
              ),
            }),
        ]"
        :disabledMessage="$t('earlyBookingOverwriteModal.noSlider')"
        @updateSliderValue="newEarlyBookingFactor = $event"
      ></override-slider>
    </template>
  </chart-overwrite-modal-base>
</template>

<script>
import Vue from 'vue'
import MaximumEarlyBookingDiscountService from '@/classes-internal/maxEarlyBookingDiscount/MaximumEarlyBookingDiscountService'
import OverwriteModalMixin from '@/components-internal/mixins/OverwriteModalMixin'
import NumberHelper from '@/classes-shared/utils/NumberHelper'
import OverrideSlider from './OverrideSlider'

export default Vue.extend({
  name: 'EarlyBookingOverwriteModalWinter3',
  components: { OverrideSlider },
  mixins: [OverwriteModalMixin],

  props: {
    earlyBookingOverwriteMetadata: {
      required: false,
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      newEarlyBookingFactor: null,
      service: new MaximumEarlyBookingDiscountService(),
      sliderStepSize: 0.001,
    }
  },

  computed: {
    showInfoBox() {
      const originalValue = parseFloat(
        this.earlyBookingOverwriteMetadata?.earlyBooking
          ?.getCurrentEarlyBookingDiscount()
          .toFixed(3)
      )
      return originalValue !== this.newEarlyBookingFactor
    },
    maxEarlyBooking() {
      return this.earlyBookingOverwriteMetadata?.earlyBooking
        ?.maxEarlyBookingDiscount
    },
    calculationSteps() {
      const prognosis = this.calculatePriceFactors(this.newEarlyBookingFactor)
      const current = this.calculatePriceFactors(
        this.earlyBookingOverwriteMetadata.earlyBooking.valdemFactor
      )
      return {
        current,
        prognosis,
      }
    },

    title() {
      const title =
        this.$i18n.t('earlyBookingOverwriteModal.title') +
        ' - ' +
        this.earlyBookingOverwriteMetadata.prodDef.getDurationDays() +
        '-' +
        this.$i18n.t('earlyBookingOverwriteModal.from') +
        ' ' +
        this.$options.filters.formatDate(
          this.earlyBookingOverwriteMetadata.earlyBooking.date
        )

      return title
    },
  },
  watch: {
    earlyBookingOverwriteMetadata: {
      handler: function (newVal) {
        if (newVal.earlyBooking) {
          this.newEarlyBookingFactor = newVal.earlyBooking
            .getCurrentEarlyBookingDiscount()
            .toFixed(3)
        }
      },
      deep: true,
    },
  },

  methods: {
    open() {
      this.$refs.baseModal?.openModal()
    },
    async overwrite() {
      await this.service.overwrite(
        this.earlyBookingOverwriteMetadata.earlyBooking,
        this.newEarlyBookingFactor
      )
      this.showModal = false
      this.$emit('updated')
    },
    async reset() {
      await this.service.reset(
        this.earlyBookingOverwriteMetadata.earlyBooking,
        this.newEarlyBookingFactor
      )
      this.showModal = false
      this.$emit('updated')
    },
    calculatePriceFactors(valdem) {
      const earlyBookingObj = this.earlyBookingOverwriteMetadata.earlyBooking
      const cashDeskPrice = earlyBookingObj.cashDeskPrice
      const valdemAbsolute = cashDeskPrice * valdem * -1
      const currentPostedPrice = cashDeskPrice + valdemAbsolute
      const currentPostedPriceRounded = NumberHelper.round(currentPostedPrice)
      const currentPostedPriceRoundDiff =
        currentPostedPrice - currentPostedPriceRounded

      return {
        cashDeskPrice,
        valdemAbsolute,
        currentPostedPrice,
        currentPostedPriceRounded,
        currentPostedPriceRoundDiff,
      }
    },
  },
})
</script>
